import React, { memo, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import cn from 'bem-cn';

import { RoutePaths } from '../../constants';

import { HeaderLogo } from './HeaderLogo';
import { HeaderSearch } from './HeaderSearch';
import { HeaderProfile } from './HeaderProfile';
import { HeaderBackButton } from './HeaderBackButton';
import { HeaderProps, HeaderElement } from './Header.types';

import './Header.scss';

const b = cn('header');

export const Header = memo((props: HeaderProps) => {
    const { elements, isLightColors = false } = props;

    const location = useLocation();

    const isSearchPage = location.pathname === RoutePaths.Search;
    const [isSearchActive, setIsSearchActive] = useState(isSearchPage);

    const handleSearchOpen = useCallback((position: 'desktop' | 'mobile') => {
        return () => {
            if (position !== 'mobile') {
                return;
            }

            setIsSearchActive(true);
        };
    }, []);

    const handleSearchClose = () => {
        setIsSearchActive(false);
    };

    const renderElement = (element: HeaderElement, position: 'desktop' | 'mobile'): React.ReactNode => {
        const elementClass = b('element', { [position]: true });

        switch (element.type) {
            case 'logo':
                return <HeaderLogo key={`${position}-logo`} className={elementClass.mix(element.className)} />;

            case 'search':
                return (
                    <HeaderSearch
                        key={`${position}-search`}
                        onSearchOpen={handleSearchOpen(position)}
                        onSearchClose={handleSearchClose}
                        isActive={isSearchPage}
                        className={elementClass}
                    />
                );

            case 'profile':
                return (
                    <HeaderProfile
                        key={`${position}-profile`}
                        className={elementClass.mix(element.className)}
                        isLightColors={isLightColors}
                    />
                );

            case 'backButton':
                const { type, ...backButtonProps } = element;

                return <HeaderBackButton key={`${position}-back`} {...backButtonProps} className={elementClass} />;

            case 'custom':
                return (
                    <div key={`${position}-custom`} className={elementClass}>
                        {element.render()}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <header className={b({ 'search-active': isSearchActive, 'light-colors': isLightColors })}>
            <div className={b('left')}>
                {elements.desktop.left && renderElement(elements.desktop.left, 'desktop')}
                {elements.mobile.left && renderElement(elements.mobile.left, 'mobile')}
            </div>
            <div className={b('center')}>
                {elements.desktop.center && renderElement(elements.desktop.center, 'desktop')}
                {elements.mobile.center && renderElement(elements.mobile.center, 'mobile')}
            </div>
            <div className={b('right')}>
                {elements.desktop.right && renderElement(elements.desktop.right, 'desktop')}
                {elements.mobile.right && renderElement(elements.mobile.right, 'mobile')}
            </div>
        </header>
    );
});
